/**
 * Credit-bridge POST drainer.
 *
 * ADR-0008 Sprint 15. A React hook that — when the bridge is enabled AND
 * the user is linked — walks the `bridgeStore.queue` and POSTs each entry
 * to the configured edge function. Success → recordGrant; failure →
 * recordFailure (which implements the 3-attempt retry cap).
 *
 * Policy:
 *   - One drain pass at a time (in-flight guard via local ref)
 *   - On 2xx with `{granted, starsAwarded}` JSON: record grant, dequeue
 *   - On 4xx other than 409: record failure (counts against retry cap)
 *   - On 409 (duplicate): treat as success with 0 credits and dequeue
 *     (ADR-0008: idempotency dedupe on the server)
 *   - On network error: record failure
 *   - Backoff between retries within a session: none (user may see multiple
 *     retries quickly); next full session picks up the queue fresh
 *
 * Drain triggers:
 *   - On mount (picks up persisted queue across reloads)
 *   - Whenever queue.length changes
 *   - Whenever linkStatus flips to 'connected'
 *   - Whenever window 'online' event fires (re-try after offline)
 */

import { useEffect, useRef } from 'react';
import { isBridgeEnabled, useBridgeStore } from './bridgeStore.ts';

/** Resolve the edge URL from env. Called at hook-mount time only. */
function resolveBridgeUrl(): string | null {
  try {
    return import.meta.env.VITE_BRIDGE_URL ?? null;
  } catch {
    return null;
  }
}

async function postOne(
  url: string,
  token: string,
  entry: ReturnType<typeof useBridgeStore.getState>['queue'][number],
): Promise<{ ok: true; granted: number } | { ok: false; reason: string }> {
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        // ADR-0008: idempotency key in header for server dedupe.
        'idempotency-key': entry.idempotencyKey,
      },
      body: JSON.stringify({
        mapId: entry.mapId,
        heroId: entry.heroId,
        seed: entry.seed,
        bestStars: entry.bestStars,
        previousBestStars: entry.previousBestStars,
        // ADR-0011: loadout + skill-script included so the edge function
        // re-simulates with the client's exact inputs.
        towers: entry.towers,
        skillScript: entry.skillScript,
      }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: `network: ${msg}` };
  }
  if (resp.status === 409) {
    // Server says we already submitted this. Treat as success, zero grant.
    return { ok: true, granted: 0 };
  }
  if (resp.status === 401) {
    return { ok: false, reason: '401 unauthenticated — link expired' };
  }
  if (resp.status >= 400) {
    return { ok: false, reason: `http ${resp.status}` };
  }
  let data: { granted?: number } = {};
  try {
    data = (await resp.json()) as { granted?: number };
  } catch {
    return { ok: false, reason: 'server returned non-JSON' };
  }
  const granted = typeof data.granted === 'number' ? data.granted : 0;
  return { ok: true, granted };
}

export function useBridgeDrainer(): void {
  const url = resolveBridgeUrl();
  const bridge = useBridgeStore;
  const inflight = useRef(false);

  useEffect(() => {
    if (!isBridgeEnabled() || !url) return;

    async function drainOnce() {
      if (inflight.current) return;
      inflight.current = true;
      try {
        let state = bridge.getState();
        while (state.linkStatus === 'connected' && state.token && state.queue.length > 0) {
          const entry = state.queue[0];
          const result = await postOne(url!, state.token, entry);
          if (result.ok) {
            state.dequeueFirst();
            if (result.granted > 0) state.recordGrant(result.granted);
          } else {
            state.recordFailure(result.reason);
            // After a failure, stop this drain pass. Next trigger will retry.
            break;
          }
          state = bridge.getState();
        }
      } finally {
        inflight.current = false;
      }
    }

    // Drain on mount.
    void drainOnce();

    // Drain whenever the queue changes OR link status flips. zustand's
    // subscribe returns an unsubscribe fn.
    const unsubscribe = bridge.subscribe((next, prev) => {
      if (
        next.queue.length !== prev.queue.length ||
        next.linkStatus !== prev.linkStatus
      ) {
        void drainOnce();
      }
    });

    // Drain when connectivity returns after offline.
    const onOnline = () => void drainOnce();
    window.addEventListener('online', onOnline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', onOnline);
    };
  }, [url, bridge]);
}
